import { ItemsPerPage, Table } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { Row } from 'react-table'

import { SortDirection } from '../../../../../../enums/sortDirection'
import { getTableColumnIdDirection } from '../../../../../../utils/getTableColumnIdDirection'
import { QuotesSortProperty } from '../../../../enums/quotesSortProperty'
import { useQuotesList } from '../../context/quotesList'
import { useQuotesListData } from '../../context/quotesListData'
import { useQuotesListFilters } from '../../context/quotesListFilters'
import { QuotesTableRow } from '../../types/quotesTableRow'
import { QuotesTableEmptyRowInfo } from './elements/QuotesTableEmptyRowInfo'
import { useQuotesTableColumns } from './hooks/useQuotesTableColumns'

export const QuotesTable = (): ReactElement => {
  const { tableId } = useQuotesList()
  const [{ pageSize, sortDirection, sortProperty }, setFilters] = useQuotesListFilters()
  const { data: quotesData, isLoading, onSelectRow, pagination } = useQuotesListData()

  const { columns } = useQuotesTableColumns()

  const sortedColumnId = useMemo(
    () => getTableColumnIdDirection(sortProperty, sortDirection === SortDirection.Desc),
    [sortDirection, sortProperty],
  )

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage: ItemsPerPage) => {
      setFilters({
        page: 1,
        pageSize: itemsPerPage,
      })
    },
    [setFilters],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      setFilters({
        page,
      })
    },
    [setFilters],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setFilters({
        page: 1,
        sortProperty: columnId as QuotesSortProperty,
        sortDirection: isDesc ? SortDirection.Desc : SortDirection.Asc,
      })
    },
    [setFilters],
  )

  const handleRowClick = useCallback(
    (row: Row<QuotesTableRow>) => {
      onSelectRow(row.original)
    },
    [onSelectRow],
  )

  return (
    <Table
      columns={columns}
      currentPage={pagination?.page}
      data={quotesData}
      emptyRowInfo={<QuotesTableEmptyRowInfo />}
      id={tableId}
      isLoading={isLoading}
      itemsPerPage={pageSize}
      onItemsPerPageChange={handleItemsPerPageChange}
      onPageChange={handlePageChange}
      onRowClick={handleRowClick}
      onSort={handleSort}
      sortedColumnId={sortedColumnId}
      totalPageItems={pagination?.total}
      withColumnsFiltering
      withItemsPerPageSelect
      withStickyHeader
    />
  )
}
